import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';

import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzAlertModule } from 'ng-zorro-antd/alert';

import { DefinitionControllerService } from '@rapider/sdk';
import { NotificationService } from '@rapider/services';
import { DataTemplatesPreviewComponent } from '../data-templates-preview/data-templates-preview.component';

import { ONBOARDING_CONFIG, buildProcessTemplatePayload } from '../onboarding.config';

/**
 * @class ProjectModelLibraryIntegrationComponent
 * @description Orchestrates the workflow for reviewing and integrating a Template Library into the Active Project.
 * It utilizes the DataTemplatesPreviewComponent to show the template, and then physically triggers the API to import the customized schema.
 */
@Component({
  selector: 'rapider-project-model-library-integration',
  standalone: true,
  imports: [
    CommonModule,
    NzButtonModule,
    NzAlertModule,
    DataTemplatesPreviewComponent
  ],
  templateUrl: './project-model-library-integration.component.html',
  host: {
    class: 'block h-full w-full bg-body-background overflow-y-auto custom-scrollbar'
  }
})
export class ProjectModelLibraryIntegrationComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly definitionController = inject(DefinitionControllerService);
  private readonly notificationService = inject(NotificationService);

  public readonly config = ONBOARDING_CONFIG;

  // View State Signals
  public isLoading = signal<boolean>(true);
  public uiState = signal<'confirm' | 'processing' | 'success' | 'error'>('confirm');

  // Data Signals
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public templateData = signal<any>(null);

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.fetchTemplate(id);
    } else {
      this.goBack();
    }
  }

  /**
   * Fetches the template definition from the backend via its ID.
   */
  private async fetchTemplate(id: string): Promise<void> {
    try {
      this.isLoading.set(true);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const template = await firstValueFrom(this.definitionController.findById({ id }) as any);
      if (template) {
        this.templateData.set(template);
      } else {
        throw new Error('Template not found');
      }
    } catch (err) {
      this.notificationService.createNotification('error', 'Error', this.config.notifications.fetchPreviewFailed);
      this.goBack();
    } finally {
      this.isLoading.set(false);
    }
  }

  /**
   * Confirms the finalized architecture and physically triggers the API to import the customized schema.
   */
  public async applyIntegration(): Promise<void> {
    this.uiState.set('processing');

    try {
      const originalTemplate = this.templateData();
      const payload = buildProcessTemplatePayload(originalTemplate.id);

      await firstValueFrom(this.definitionController.processDataModelTemplate(payload));

      this.uiState.set('success');
      this.notificationService.createNotification('success', 'Success', this.config.notifications.addSuccess);
    } catch (error) {
      console.error('[Integration Component] Failed to apply templates', error);
      this.uiState.set('error');
      this.notificationService.createNotification('error', 'Error', this.config.notifications.addFailed);
    }
  }

  /**
   * Navigates to the database page after successful or failed integration.
   */
  public navigateToDatabase(): void {
    this.router.navigate(['/database']);
  }

  /**
   * Retries integration by resetting to the confirmation state.
   */
  public retryIntegration(): void {
    this.uiState.set('confirm');
  }

  /**
   * Navigates back to the template details page.
   */
  public goBack(): void {
    const id = this.templateData()?.id;
    if (id) {
      this.router.navigate(['/data-templates', id]);
    } else {
      this.router.navigate(['/data-templates']);
    }
  }
}